import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { studentService } from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';

const StudentList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);

  const grades = ['1A', '1B', '2', '3', '4', '5', '6&7', '8&9', '10', 'Prescolar'];
  const statuses = [
    { value: 'active', label: 'Activo', color: 'success' },
    { value: 'inactive', label: 'Inactivo', color: 'default' },
    { value: 'baja', label: 'Baja', color: 'error' },
    { value: 'finalizado', label: 'Finalizado', color: 'info' },
    { value: 'exento', label: 'Exento', color: 'warning' }
  ];

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      };
      
      if (search) params.search = search;
      if (gradeFilter) params.grade = gradeFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await studentService.getAll(params);
      
      setStudents(response.data.data);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar estudiantes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, [paginationModel, gradeFilter, statusFilter]);

  const handleSearch = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    loadStudents();
  };

  const handleDeleteClick = (student: any) => {
    setSelectedStudent(student);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedStudent) return;
    
    try {
      await studentService.delete(selectedStudent.id);
      setDeleteDialogOpen(false);
      setSelectedStudent(null);
      loadStudents();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar estudiante');
    }
  };

  const handleExport = () => {
    const data = students.map(student => ({
      'ID': student.id,
      'Nombre': `${student.first_name} ${student.last_name}`,
      'Grado': student.grade,
      'Patrocinador': student.sponsor || '',
      'Estado': student.status,
      'Padre': student.parent?.full_name || '',
      'Email Padre': student.parent?.email || '',
      'Teléfono': student.parent?.phone || '',
      'Fecha Ingreso': student.enrollment_date || ''
    }));
    
    exportToExcel(data, 'estudiantes');
  };

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70 
    },
    { 
      field: 'full_name', 
      headerName: 'Nombre Completo', 
      //width: 200,
      flex: 2,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.first_name} {params.row.last_name}
          </Typography>
          {params.row.sponsor && (
            <Typography variant="caption" color="text.secondary">
              Patrocinador: {params.row.sponsor}
            </Typography>
          )}
        </Box>
      )
    },
    { 
      field: 'grade', 
      headerName: 'Grado', 
      //width: 100 
      flex: 0.3,
    },
    {
      field: 'parent',
      headerName: 'Padre/Tutor',
      //width: 200,
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.row.parent?.russian_name || 'Sin asignar'}
          </Typography>
          {params.row.parent?.spanish_name && (
            <Typography variant="caption" color="text.secondary">
              {params.row.parent.spanish_name}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      //width: 120,
      flex: 0.7,
      renderCell: (params) => {
        const status = statuses.find(s => s.value === params.value);
        return (
          <Chip
            label={status?.label || params.value}
            color={status?.color as any || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'months_without_payment',
      headerName: 'Meses Sin Pago',
      //width: 150,
      flex: 1,
      renderCell: (params) => {
        const months = params.row.months_without_payment || 0;
        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" align="center">
              {months} {months === 1 ? 'mes' : 'meses'}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(months * 10, 100)}
              color={months >= 3 ? 'error' : months >= 2 ? 'warning' : 'success'}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      //width: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); navigate(`/students/${params.row.id}`)}}
            title="Ver detalles"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); navigate(`/students/${params.row.id}/edit`)}}
            title="Editar"
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {e.stopPropagation(); handleDeleteClick(params.row)}}
            title="Eliminar"
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Estudiantes
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/students/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Estudiante
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Buscar estudiante..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Grado</InputLabel>
              <Select
                value={gradeFilter}
                label="Grado"
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {grades.map(grade => (
                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                label="Estado"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {statuses.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleSearch}
                fullWidth
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearch('');
                  setGradeFilter('');
                  setStatusFilter('');
                }}
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={students}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/students/${params.id}`)}
          disableRowSelectionOnClick
          sortingMode="server"  // 👈 Si también ordenas en el servidor
          filterMode="server"   // 
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar al estudiante{' '}
            <strong>{selectedStudent?.first_name} {selectedStudent?.last_name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer. Se eliminarán también todos los pagos asociados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentList;